// src/pages/Pricing.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { db } from "../components/firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import Loader from "../components/common/Loader";

const staticPlans = [
  {
    name: "MONTHLY",
    description:
      "Access 25–30 new question papers every month plus study materials & chat support.",
    features: ["25–30 Question Papers", "Study Materials", "Priority Chat"],
  },
  {
    name: "HALF YEARLY",
    description:
      "Get 200+ question papers over 6 months, complete notes & full support.",
    features: ["200+ Question Papers", "Study Materials", "Priority Chat"],
  },
  {
    name: "ANNUAL",
    description:
      "Unlimited papers for a full year, all notes & 24/7 chat support included.",
    features: ["500000+ Question Papers", "Study Materials", "24/7 Chat"],
  },
];

const categories = [
  { name: "10th & 12th", prefix: "10th12th" },
  { name: "JEE", prefix: "jee" },
  { name: "NEET", prefix: "neet" },
];

const PlanCard = ({ name, price, description, features }) => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.05 }}
      className="
        max-w-sm w-full
        bg-pink-600 bg-opacity-90 p-8 rounded-3xl shadow-xl
        hover:shadow-2xl transform transition
        flex flex-col justify-between
      "
    >
      <div>
        <p className="uppercase text-sm font-semibold text-pink-200 mb-2 tracking-wide">
          {name}
        </p>
        <h3 className="text-4xl font-bold text-white mb-3">
          ₹{price}
          <span className="text-base font-medium text-pink-300 ml-2">Only</span>
        </h3>
        <p className="text-pink-100 leading-relaxed mb-6">{description}</p>
        <ul className="space-y-3">
          {features.map((feat, i) => (
            <li key={i} className="flex items-center text-pink-200">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 flex-shrink-0 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <circle cx="10" cy="10" r="9" fill="rgba(255,255,255,0.2)" />
                <path
                  fillRule="evenodd"
                  d="M14.707 7.293a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                  className="text-white"
                />
              </svg>
              {feat}
            </li>
          ))}
        </ul>
      </div>
      <button
        onClick={() =>
          (window.location.href = "https://wa.me/qr/DO3TUISQWWSCH1")
        }
        className="
          mt-8 py-3 bg-gradient-to-r from-pink-500 to-pink-400
          text-white font-semibold rounded-xl
          hover:from-pink-400 hover:to-pink-500 transition
        "
      >
        Choose Plan
      </button>
    </motion.div>
  );
};

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPricing() {
      try {
        const snap = await getDocs(collection(db, "pricing"));
        setPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    }
    fetchPricing();
  }, []);

  // merge staticPlans + firestore data
  const combined = [];
  categories.forEach(cat =>
    staticPlans.forEach(sp => {
      const key = sp.name.toLowerCase().replace(/ /g, "");
      const doc = plans.find(p =>
        p.id.startsWith(cat.prefix) &&
        (p[key] !== undefined || p.montly !== undefined)
      );
      if (doc) {
        combined.push({
          ...sp,
          price: doc[key] ?? doc.montly,
          category: cat.name,
        });
      }
    })
  );

  const byCat = combined.reduce((acc, plan) => {
    (acc[plan.category] ||= []).push(plan);
    return acc;
  }, {});

  if (loading) return <Loader />;

  return (
    <section className="bg-pink-50 py-16">
      <div className="max-w-5xl mx-auto px-4">
        <motion.div
          className="text-center mb-12"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-5xl font-extrabold text-gray-800">
            Plans{" "}
            <motion.span
              initial={{ color: "#222" }}
              animate={{ color: "#ec4899" }}
              transition={{ duration: 1 }}
            >
              We
            </motion.span>{" "}
            Offer
          </h2>
          <p className="text-pink-600 mt-2">
            Choose the best plan that suits your needs.
          </p>
        </motion.div>

        {Object.entries(byCat).map(([catName, items], idx) => (
          <div key={idx} className="mb-16">
            <h3 className="text-3xl font-semibold text-center text-pink-600 mb-8">
              {catName}
            </h3>
            {/* center all cards */}
            <div className="flex flex-wrap justify-center gap-8">
              {items.map((plan, i) => (
                <PlanCard key={i} {...plan} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Pricing;
